import { useMemo } from 'react';
import {
  PlusCircleIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export interface AuditLogEntry {
  id: string;
  action: 'created' | 'updated';
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
}

// Grouped entries that happened at the same time
interface GroupedAuditEntry {
  id: string;
  action: 'created' | 'updated';
  changedAt: string;
  // For created: single entry with title
  title?: string;
  // For updated: list of field changes
  changes?: {
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
  }[];
}

interface AuditLogFeedProps {
  entries: AuditLogEntry[];
  loading?: boolean;
  compact?: boolean;
}

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

// Group entries that happened within 3 seconds of each other
function groupEntriesByTime(entries: AuditLogEntry[]): GroupedAuditEntry[] {
  const grouped: GroupedAuditEntry[] = [];
  let currentGroup: GroupedAuditEntry | null = null;
  let currentTime: number | null = null;

  for (const entry of entries) {
    const entryTime = new Date(entry.changedAt).getTime();

    if (entry.action === 'created') {
      // Created entries are always standalone
      grouped.push({
        id: entry.id,
        action: 'created',
        changedAt: entry.changedAt,
        title: entry.newValue || undefined,
      });
      currentGroup = null;
      currentTime = null;
    } else {
      // Updated entries - group if within 3 seconds
      const shouldGroup =
        currentGroup &&
        currentTime &&
        Math.abs(entryTime - currentTime) < 3000;

      if (shouldGroup && currentGroup) {
        // Add to existing group
        currentGroup.changes?.push({
          fieldName: entry.fieldName || 'Unknown',
          oldValue: entry.oldValue,
          newValue: entry.newValue,
        });
      } else {
        // Start new group
        currentGroup = {
          id: entry.id,
          action: 'updated',
          changedAt: entry.changedAt,
          changes: [
            {
              fieldName: entry.fieldName || 'Unknown',
              oldValue: entry.oldValue,
              newValue: entry.newValue,
            },
          ],
        };
        grouped.push(currentGroup);
        currentTime = entryTime;
      }
    }
  }

  return grouped;
}

function ChangeRow({
  fieldName,
  oldValue,
  newValue,
  compact,
}: {
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  compact?: boolean;
}) {
  const textClass = compact ? 'text-xs' : 'text-sm';
  return (
    <div className={`flex items-start gap-1.5 ${textClass} py-0.5`}>
      <span className="text-text-tertiary">•</span>
      <span className={`text-text-secondary font-medium ${compact ? 'min-w-[60px]' : 'min-w-[80px]'}`}>
        {fieldName}:
      </span>
      <div className="flex items-center gap-1 flex-wrap">
        {oldValue && (
          <span className="text-text-tertiary line-through">{oldValue}</span>
        )}
        {oldValue && newValue && (
          <ArrowRightIcon className="w-3 h-3 text-text-tertiary flex-shrink-0" />
        )}
        {newValue && (
          <span className="text-text-primary">{newValue}</span>
        )}
        {!oldValue && newValue && (
          <span className="text-green-600 text-xs">(added)</span>
        )}
        {oldValue && !newValue && (
          <span className="text-red-600 text-xs">(removed)</span>
        )}
      </div>
    </div>
  );
}

function AuditLogCard({ entry, compact }: { entry: GroupedAuditEntry; compact?: boolean }) {
  const isCreated = entry.action === 'created';

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${compact ? 'p-2.5' : 'p-4'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${compact ? 'mb-1' : 'mb-2'}`}>
        <div className="flex items-center gap-1.5">
          {isCreated ? (
            <PlusCircleIcon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-green-500`} />
          ) : (
            <PencilSquareIcon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-blue-500`} />
          )}
          <span className={`font-medium text-text-primary ${compact ? 'text-xs' : 'text-sm'}`}>
            {isCreated ? 'Initiative created' : 'Initiative updated'}
          </span>
        </div>
        <span className="text-xs text-text-tertiary">
          {formatRelativeTime(entry.changedAt)}
        </span>
      </div>

      {/* Created: show title */}
      {isCreated && entry.title && (
        <div className={`bg-green-50 rounded-md ${compact ? 'p-2 mt-1' : 'p-3 mt-2'}`}>
          <span className={`${compact ? 'text-xs' : 'text-sm'} text-green-700`}>{entry.title}</span>
        </div>
      )}

      {/* Updated: show list of changes */}
      {!isCreated && entry.changes && entry.changes.length > 0 && (
        <div className={`bg-gray-50 rounded-md ${compact ? 'px-2 py-1 mt-1' : 'px-3 py-2 mt-2'}`}>
          {entry.changes.map((change, index) => (
            <ChangeRow
              key={index}
              fieldName={change.fieldName}
              oldValue={change.oldValue}
              newValue={change.newValue}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AuditLogFeed({ entries, loading, compact }: AuditLogFeedProps) {
  // Group entries by time, then by date
  const groupedByDate = useMemo(() => {
    const timeGrouped = groupEntriesByTime(entries);
    const groups: { date: string; entries: GroupedAuditEntry[] }[] = [];
    let currentDate = '';

    for (const entry of timeGrouped) {
      const entryDate = new Date(entry.changedAt).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });

      if (entryDate !== currentDate) {
        currentDate = entryDate;
        groups.push({ date: entryDate, entries: [] });
      }

      groups[groups.length - 1].entries.push(entry);
    }

    return groups;
  }, [entries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <PencilSquareIcon className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-text-secondary text-sm">No changes recorded yet</p>
        <p className="text-text-tertiary text-xs mt-1">
          Changes to this initiative will appear here
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {groupedByDate.map((group) => (
        <div key={group.date}>
          {/* Date header */}
          <div className={`text-xs font-medium text-text-tertiary uppercase tracking-wide ${compact ? 'mb-2' : 'mb-3'}`}>
            {group.date}
          </div>
          {/* Cards */}
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            {group.entries.map((entry) => (
              <AuditLogCard key={entry.id} entry={entry} compact={compact} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
